import { useEffect } from 'react';

interface DynamicMetaProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  siteName?: string;
}

const DynamicMeta: React.FC<DynamicMetaProps> = ({
  title = "Someone shared something with you on Hooper",
  description = "Check out what someone shared with you",
  image = "/logos/logo-white.svg",
  url,
  type = "website",
  siteName = "Hooper"
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement;
      
      if (element) {
        element.content = content;
      } else {
        element = document.createElement('meta');
        if (isProperty) {
          element.setAttribute('property', property);
        } else {
          element.setAttribute('name', property);
        }
        element.content = content;
        document.head.appendChild(element);
      }
    };

    // Update basic meta tags
    updateMetaTag('description', description);

    // Update Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', siteName, true);
    
    // Handle image URL - make it absolute if it's relative
    let imageUrl = image.startsWith('http') 
      ? image 
      : `${window.location.origin}${image.startsWith('/') ? image : `/${image}`}`;
    
    // If the image is an SVG (not well supported by social platforms), use PNG fallback
    if (imageUrl.endsWith('.svg')) {
      imageUrl = `https://app.hooper.gg/logo512.png`;
    }
    
    updateMetaTag('og:image', imageUrl, true);
    updateMetaTag('og:image:alt', `${siteName} - ${title}`, true);
    
    // Handle URL
    if (url) {
      const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      updateMetaTag('og:url', fullUrl, true);
    } else {
      updateMetaTag('og:url', window.location.href, true);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', imageUrl);
    updateMetaTag('twitter:site', '@HooperApp');

    // Facebook specific tags
    updateMetaTag('fb:app_id', '6834056016613846', true); 

    // Additional tags for better rich previews
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:locale', 'en_US', true);

  }, [title, description, image, url, type, siteName]);

  return null; // This component doesn't render anything
};

export default DynamicMeta;
